import React, { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RegistrationForm = React.forwardRef((_, ref) => {
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const errs = {};
    if (!firstName.trim()) errs.firstName = "Please share your first name.";
    if (!email.trim()) {
      errs.email = "An email is required to send the Zoom link.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      errs.email = "Please enter a valid email address.";
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!validate()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/register`, {
        first_name: firstName.trim(),
        email: email.trim().toLowerCase(),
      });
      navigate("/thank-you", { state: { firstName: firstName.trim() } });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(
        typeof detail === "string"
          ? detail
          : "Something went gently wrong. Please try again."
      );
      setSubmitting(false);
    }
  };

  return (
    <section
      ref={ref}
      id="register"
      className="py-24 md:py-36 bg-alabaster relative"
      data-testid="registration-section"
    >
      <div className="max-w-3xl mx-auto px-6 md:px-12">
        <div className="text-center fade-up">
          <p className="eyebrow" data-testid="registration-overline">
            Save Your Seat
          </p>
          <h2
            className="mt-6 font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight balance"
            style={{ color: "var(--ink)" }}
            data-testid="registration-headline"
          >
            Register for the{" "}
            <em style={{ color: "var(--gold-deep)" }}>next masterclass</em>
          </h2>
          <p className="mt-5 font-serif italic" style={{ color: "var(--ink)", fontSize: "1.3rem" }}>
            Do Less. Be More.{" "}
            <span style={{ color: "var(--gold-deep)" }}>
              Wednesday 18th June · 7pm BST
            </span>
          </p>
          <p className="mt-4 text-[color:var(--ink-soft)]" style={{ fontSize: "1.15rem" }}>
            Enter your details below and we&apos;ll send your Zoom link by email.
          </p>
        </div>

        <form
            onSubmit={onSubmit}
            noValidate
            className="mt-14 md:mt-16 space-y-10 fade-up delay-200"
            data-testid="registration-form"
          >
            <div>
              <label
                htmlFor="firstName"
                className="eyebrow block mb-3"
                style={{ fontSize: "0.62rem" }}
              >
                First Name
              </label>
              <input
                id="firstName"
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Your first name"
                className="luxe-input"
                autoComplete="given-name"
                disabled={submitting}
                aria-invalid={!!errors.firstName}
                data-testid="registration-firstname-input"
              />
              {errors.firstName && (
                <p
                  className="mt-2 text-xs italic"
                  style={{ color: "#9C5252" }}
                  data-testid="registration-firstname-error"
                >
                  {errors.firstName}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="email"
                className="eyebrow block mb-3"
                style={{ fontSize: "0.62rem" }}
              >
                Email Address
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="luxe-input"
                autoComplete="email"
                disabled={submitting}
                aria-invalid={!!errors.email}
                data-testid="registration-email-input"
              />
              {errors.email && (
                <p
                  className="mt-2 text-xs italic"
                  style={{ color: "#9C5252" }}
                  data-testid="registration-email-error"
                >
                  {errors.email}
                </p>
              )}
            </div>

            {error && (
              <p
                className="text-sm text-center italic"
                style={{ color: "#9C5252" }}
                data-testid="registration-server-error"
              >
                {error}
              </p>
            )}

            <div className="flex flex-col items-center pt-4">
              <button
                type="submit"
                className="btn-primary w-full md:w-auto md:px-16"
                disabled={submitting}
                data-testid="registration-submit-btn"
              >
                {submitting ? "Saving your seat…" : "Save My Seat"}
              </button>
              <p
                className="mt-6 text-xs text-[color:var(--ink-soft)] tracking-wider"
                data-testid="registration-privacy-note"
              >
                We respect your privacy. Unsubscribe at any time.
              </p>
            </div>
          </form>

      </div>
    </section>
  );
});

RegistrationForm.displayName = "RegistrationForm";

export default RegistrationForm;
